import React, { useEffect, useState } from 'react';
import { ChevronRight, ChevronDown } from 'lucide-react';
import { fetchCalendarComparison } from '../services/revenueService';
import { useAuth } from '../context/useAuth';

const monthOrder = {
  'January': 1, 'February': 2, 'March': 3, 'April': 4, 'May': 5, 'June': 6,
  'July': 7, 'August': 8, 'September': 9, 'October': 10, 'November': 11, 'December': 12
};

const CalendarComparisonTable = ({ year }) => {
  const [reportGroups, setReportGroups] = useState({});
  const [subcounties, setSubcounties] = useState([]);
  const [expandedStreams, setExpandedStreams] = useState({});
  const [loading, setLoading] = useState(true);
  const { user } = useAuth(); // Access user from context
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const result = await fetchCalendarComparison(year);
        const grouped = result.data.reduce((acc, row) => {
          const stream = row.rev_stream || 'UNKNOWN';
          if (!acc[stream]) acc[stream] = [];
          acc[stream].push(row);
          return acc;
        }, {});

        Object.keys(grouped).forEach(key => {
          grouped[key].sort((a, b) => monthOrder[a.calendar_month] - monthOrder[b.calendar_month]);
        });
        
        setReportGroups(grouped);
        const allowedSubcounties = user?.sub_county_id 
        ? result.subcounties.filter(sc => sc.id === user.sub_county_id)
        : result.subcounties;
        setSubcounties(allowedSubcounties);
      } catch (err) {
        console.error("Calendar Load error:", err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [year, user?.sub_county_id]);

  if (loading) return <div className="p-10 text-center">Loading Calendar Year Data...</div>;

  const allData = Object.values(reportGroups).flat();

  return (
    <div className="relative overflow-x-auto shadow-lg rounded-xl border border-gray-200 bg-white">
      <table className="w-full text-sm text-left text-gray-600">
        <thead className="text-xs text-gray-700 uppercase bg-gray-100 border-b">
          <tr>
            <th className="px-6 py-4 font-bold sticky left-0 bg-gray-100 z-20">Revenue Stream</th>
            <th className="px-6 py-4 font-bold text-blue-800">Total Year</th>
             {subcounties.map(sc => (
                <th 
                  key={sc.id} 
                  className="px-6 py-4 border-l cursor-help underline decoration-dotted decoration-slate-300 hover:bg-gray-200 transition-colors"
                  title={`RO: ${sc.username || 'Not Assigned'}`}
                >
                  {sc.name}
                </th>
              ))}
          </tr>
        </thead>
        <tbody>
          {Object.entries(reportGroups).map(([streamName, months]) => {
            const totalStream = months.reduce((s, m) => s + m.total_county, 0);
            const isExpanded = expandedStreams[streamName];

            return (
              <React.Fragment key={streamName}>
                <tr className="bg-white border-b hover:bg-gray-50 cursor-pointer" onClick={() => setExpandedStreams(p => ({...p, [streamName]: !p[streamName]}))}>
                  <td className="px-6 py-4 sticky left-0 bg-white z-10 font-bold flex items-center border-r">
                    {isExpanded ? <ChevronDown size={18} className="mr-2 text-blue-500" /> : <ChevronRight size={18} className="mr-2 text-gray-400" />}
                    {streamName}
                  </td>
                  <td className="px-6 py-4 text-blue-700 font-bold">{totalStream.toLocaleString()}</td>
                  {subcounties.map(sc => (
                    <td key={sc.id} className="px-6 py-4 font-semibold border-l">
                      {months.reduce((s, m) => s + (m[sc.name] || 0), 0).toLocaleString()}
                    </td>
                  ))}
                </tr>
                {isExpanded && months.map((m, i) => (
                  <tr key={i} className="bg-gray-50/50 border-b text-xs italic text-gray-500">
                    <td className="px-12 py-2 sticky left-0 bg-gray-50 border-r">{m.calendar_month}</td>
                    <td className="px-6 py-2">{m.total_county.toLocaleString()}</td>
                    {subcounties.map(sc => <td key={sc.id} className="px-6 py-2 border-l">{(m[sc.name] || 0).toLocaleString()}</td>)}
                  </tr>
                ))}
              </React.Fragment>
            );
          })}
          <tr className="bg-blue-100 font-extrabold border-t-2 border-blue-200">
            <td className="px-6 py-4 sticky left-0 bg-blue-100 z-10">GRAND TOTAL</td>
            <td className="px-6 py-4 text-blue-800">{allData.reduce((s, m) => s + m.total_county, 0).toLocaleString()}</td>
            {subcounties.map(sc => (
              <td key={sc.id} className="px-6 py-4 text-blue-800 border-l">
                {allData.reduce((s, m) => s + (m[sc.name] || 0), 0).toLocaleString()}
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default CalendarComparisonTable;